// LLM generated - Model Claude 4 Sonnet
import { useCallback, useContext, useMemo } from 'react';

import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { ContestAlertManager } from '~/domains/contest/domains/common/ContestAlertManager';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

/**
 * Custom hook for handling service fee modal logic and navigation blocking in ContestCard components
 *
 * This hook centralizes all service fee-related logic including:
 * - Determining if navigation should be blocked due to service fee issues
 * - Opening service fee modals when appropriate
 * - Handling card click events with service fee logic
 *
 * @param contest - The contest to check for service fee alerts
 * @param shouldBlockForServiceFee - Whether to enable service fee blocking logic
 * @returns Object with shouldBlockNavigation boolean and handleCardClick function
 */
export function useServiceFeeModalHandler(
  contest: Contest | MyUserContest | null,
  shouldBlockForServiceFee = false
) {
  const { openModal } = useContext(RootModalsContext);

  /**
   * Memoized ContestAlertManager instance to avoid recreating on every render
   */
  const contestAlertManager = useMemo(() => {
    if (!contest) return null;
    const myUserContest = contest as MyUserContest;
    if (!myUserContest.alerts) return null;
    return new ContestAlertManager(myUserContest.alerts);
  }, [contest]);

  /**
   * Memoized check for whether navigation should be blocked due to service fee issues
   */
  const shouldBlockNavigation = useMemo(() => {
    if (!shouldBlockForServiceFee || !contestAlertManager) return false;

    const hasServiceFeeIssues =
      contestAlertManager.hasServiceFeeOverdue() || contestAlertManager.hasServiceFeeCancelled();

    return hasServiceFeeIssues;
  }, [shouldBlockForServiceFee, contestAlertManager]);

  /**
   * Opens service fee modal if contest has service fee issues
   *
   * @param event - The mouse event to potentially prevent
   * @returns boolean - true if modal was opened (navigation prevented), false otherwise
   */
  const openServiceFeeModal = useCallback(
    (event: React.MouseEvent): boolean => {
      if (!contest || !shouldBlockNavigation || !contestAlertManager) return false;

      const myUserContest = contest as MyUserContest;

      // Only check entertainment-only contests with alerts
      if (!myUserContest?.isEntertainmentOnly) {
        return false;
      }

      const isServiceFeeOverdue = contestAlertManager.hasServiceFeeOverdue();
      const isServiceFeeCancelled = contestAlertManager.hasServiceFeeCancelled();

      // Open modal for overdue or cancelled service fee alerts
      if (isServiceFeeOverdue || isServiceFeeCancelled) {
        const serviceFeeAlert = contestAlertManager.getFirstServiceFeeAlert();
        event.preventDefault(); // Prevent navigation

        openModal({
          type: 'ServiceFeeModal',
          alertType: serviceFeeAlert.type,
          commissionerName:
            myUserContest.commissioner?.displayName || myUserContest.commissioner?.handle,
          commissionerUsername: myUserContest.commissioner?.username,
        });

        return true; // Modal was opened
      }

      return false; // No modal needed
    },
    [contest, shouldBlockNavigation, contestAlertManager, openModal]
  );

  /**
   * Complete card click handler that includes service fee modal logic
   *
   * This function should be called at the beginning of the card's click handler
   * to check for service fee issues and open modals when needed.
   *
   * @param event - The mouse event
   * @returns boolean - true if service fee modal was opened (should prevent further navigation)
   */
  const handleCardClick = useCallback(
    (event: React.MouseEvent): boolean => {
      // Service fee modal logic when blocking is enabled
      if (shouldBlockForServiceFee && contest && shouldBlockNavigation) {
        const modalOpened = openServiceFeeModal(event);
        if (modalOpened) {
          // Modal opened, prevent all further navigation
          return true;
        }
      }

      return false; // Continue with normal navigation
    },
    [shouldBlockForServiceFee, contest, shouldBlockNavigation, openServiceFeeModal]
  );

  return {
    shouldBlockNavigation,
    handleCardClick,
  };
}
